import { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Trans, t } from '@lingui/macro'
import { Box, Typography, Slider, Alert, Button, Stack } from '@mui/material'

import TextInputController from '@/components/ui/inputs/TextInputController'

import { useSessionSettingsForm } from '../model/sessionSettingsForm'
import { useGetPublicSessionTimeoutQuery } from '@/core/services/session-settings'

export function SessionSettings() {
  const { data: settings } = useGetPublicSessionTimeoutQuery()

  const initialValues = useMemo(() => ({ inactivityTimeoutMinutes: settings?.inactivityTimeoutMinutes ?? 15 }), [settings])

  const { isSubmitting, rhForm, submitHandler } = useSessionSettingsForm({ initialValues })
  const {
    formState: { isDirty },
  } = rhForm

  const inactivityTimeoutMinutes = rhForm.watch('inactivityTimeoutMinutes')

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        <Trans>Automatic logout after inactivity</Trans>
      </Typography>
      <Typography variant="body1" gutterBottom>
        <Trans>Users are automatically signed out after the configured period of inactivity. Applies to patients and professionals.</Trans>
      </Typography>

      <Box sx={{ mt: 3 }}>
        <form onSubmit={rhForm.handleSubmit(submitHandler)}>
          <Typography gutterBottom>
            <Trans>Automatically sign users out after {inactivityTimeoutMinutes} minutes of inactivity</Trans>
          </Typography>
          <Controller
            control={rhForm.control}
            name="inactivityTimeoutMinutes"
            render={({ field: { value } }) => (
              <Slider
                value={value}
                onChange={(_event, newValue) =>
                  rhForm.setValue('inactivityTimeoutMinutes', Array.isArray(newValue) ? newValue[0] : newValue, { shouldDirty: true })
                }
                min={5}
                max={120}
                step={1}
                marks={[
                  { value: 5, label: '5m' },
                  { value: 15, label: '15m' },
                  { value: 30, label: '30m' },
                  { value: 60, label: '60m' },
                  { value: 120, label: '120m' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => t`${value}m`}
              />
            )}
          />
          <TextInputController
            control={rhForm.control}
            fullWidth
            inputProps={{ min: 1, max: 120, step: 1 }}
            label={t`Minutes of inactivity`}
            name="inactivityTimeoutMinutes"
            sx={{ mt: 2 }}
            type="number"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Trans>Move the slider or enter a value, then click Update to change the inactivity timeout.</Trans>
          </Alert>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" disabled={!isDirty} type="submit">
              {isSubmitting ? t`Saving...` : t`Update`}
            </Button>
          </Stack>
        </form>
      </Box>
    </Box>
  )
}
